<template>
  <div class="plugin-pricing-container">
    <!-- 顶部装饰光晕 -->
    <div class="bg-glow bg-glow--blue" aria-hidden="true"></div>
    <div class="bg-glow bg-glow--orange" aria-hidden="true"></div>

    <!-- 顶部标题区域 -->
    <section class="pricing-header">
      <div class="header-content">
        <div class="header-eyebrow">PLUGIN PRICING</div>
        <h1 class="pricing-title">{{ plugin.name }} - 定价方案</h1>
        <p class="pricing-subtitle">选择最适合您需求的方案，开启高效工作体验</p>
      </div>
    </section>

    <!-- 定价周期选择 -->
    <section class="pricing-content">
      <div class="period-selection">
        <div class="period-options">
          <div
              v-for="period in periodOptions"
              :key="period.days"
              class="period-option"
              :class="{
              active: selectedDays === period.days,
              'free-option': period.type === 'free',
              'popular-option': period.popular
            }"
              @click="onPeriodChange(period.days)"
          >
            <!-- 顶部装饰条 -->
<!--            <span class="card-top-line" aria-hidden="true"></span>-->

            <!-- 热门标签 -->
            <div class="period-badge" v-if="period.badge">
              {{ period.badge }}
            </div>

            <!-- 周期标题 -->
            <div class="period-header">
              <div class="period-desc">{{ period.desc }}</div>
              <div class="period-duration">{{ period.duration }}</div>
            </div>

            <!-- 价格区域 -->
            <div class="price-section" v-if="period.type === 'paid'">
              <div class="original-price" v-if="period.originalPrice > period.currentPrice">
                ¥{{ period.originalPrice.toFixed(2) }}
              </div>
              <div class="current-price">
                <span class="price-symbol">¥</span>{{ period.currentPrice.toFixed(2) }}
              </div>
              <div class="savings">
                {{ period.savings }}
              </div>
            </div>

            <div class="price-section" v-else>
              <div class="free-price">免费体验</div>
              <div class="savings savings--free">{{ period.savings }}</div>
            </div>

            <!-- 功能特性 -->
            <ul class="features-list">
              <li v-for="(feature, index) in period.features" :key="index">
                <span class="feature-check" aria-hidden="true">
                  <el-icon><Check /></el-icon>
                </span>
                <span class="feature-text">{{ feature }}</span>
              </li>
            </ul>

            <!-- 操作按钮 -->
            <div class="action-button">
              <span v-if="period.type === 'free'">立即下载体验</span>
              <span v-else>立即购买</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 登录模态框 -->
    <LoginModal
        v-model="showLoginModal"
        :payment-info="currentPaymentInfo"
        @login-success="handleLoginSuccess"
        @switch-to-register="switchToRegister"
    />

    <!-- 注册模态框 -->
    <RegisterModal
        v-model="showRegisterModal"
        :payment-info="currentPaymentInfo"
        @register-success="handleRegisterSuccess"
        @switch-to-login="switchToLogin"
    />

    <!-- 支付弹窗 -->
    <el-dialog
        v-model="showPayDialog"
        width="380px"
        :before-close="handleClose"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        class="pricing-pay-dialog"
    >
      <!-- 自定义头部插槽 -->
      <template #header>
        <div class="pay-dialog-header">
          <div class="header-left">
            <span class="plan-name">{{ getSelectedPeriod().desc }}</span>
            <span class="plan-duration">{{ getSelectedPeriod().duration }}</span>
          </div>
          <div class="header-right">
            <span class="plan-price">¥{{ calculatePrice(selectedDays) }}</span>
            <el-icon class="close-btn" @click="closeDialog">
              <Close />
            </el-icon>
          </div>
        </div>
      </template>

      <!-- 弹窗内容 -->
      <div class="pay-dialog-body">
        <!-- 微信支付标识 -->
        <div class="wechat-pay-header">
          <svg class="wechat-icon" viewBox="0 0 1024 1024" width="24" height="24">
            <path d="M1024 619.52c0-143.36-138.24-256-307.2-256s-307.2 112.64-307.2 256 138.24 256 307.2 256c30.72 0 61.44-5.12 92.16-10.24l97.28 51.2-25.6-76.8c87.04-51.2 143.36-128 143.36-220.16z m-414.72-40.96c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z m209.92 0c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z" fill="#22c55e"/>
            <path d="M358.4 609.28c0-158.72 153.6-286.72 348.16-286.72h15.36c-40.96-133.12-179.2-235.52-353.28-235.52-204.8 0-368.64 138.24-368.64 307.2 0 107.52 66.56 204.8 168.96 256l-30.72 92.16L256 686.08c35.84 10.24 71.68 15.36 112.64 15.36h10.24c-15.36-30.72-20.48-61.44-20.48-92.16z m138.24-414.72c35.84 0 66.56 30.72 66.56 66.56s-30.72 66.56-66.56 66.56C460.8 322.56 430.08 291.84 430.08 256S460.8 194.56 496.64 194.56zM245.76 322.56c-35.84 0-61.44-30.72-61.44-66.56s30.72-66.56 66.56-66.56 61.44 30.72 61.44 66.56-30.72 66.56-66.56 66.56z" fill="#22c55e"/>
          </svg>
          <span class="pay-label">微信支付</span>
          <span class="pay-status" v-if="paymentStatus === 'success'">· 支付成功</span>
        </div>

        <!-- 二维码区域 -->
        <div class="qrcode-wrapper">
          <div class="qrcode-container">
            <!-- 二维码生成前，仅显示加载动画（无文字） -->
            <div v-if="!payQrCode && paymentStatus === 'pending'" class="qr-placeholder">
              <el-icon class="is-loading" :size="44">
                <Loading />
              </el-icon>
            </div>

            <!-- 二维码生成后，显示图片 + 遮罩 -->
            <template v-else>
              <img :src="payQrCode" alt="微信支付二维码" class="qrcode-image" />

              <!-- 遮罩层（加载/过期/成功） -->
              <div class="qr-mask" v-if="qrLoading || paymentStatus === 'expired' || paymentStatus === 'success'">

                <!-- 加载遮罩 -->
                <div v-if="qrLoading" class="mask-spinner">
                  <el-icon class="is-loading" :size="32">
                    <Loading />
                  </el-icon>
                </div>

                <!-- 过期遮罩 -->
                <div v-else-if="paymentStatus === 'expired'" class="expired-mask-content" @click="handleRegenerate">
                  <el-icon class="expired-icon"><WarningFilled /></el-icon>
                  <span class="expired-text">二维码已过期</span>
                </div>

                <!-- 支付成功遮罩 -->
                <div v-else-if="paymentStatus === 'success'" class="success-mask">
                  <div class="success-circle">
                    <el-icon color="#ffffff" :size="40"><Check /></el-icon>
                  </div>
                  <span class="success-text">支付成功</span>
                  <span class="success-sub">即将开始下载...</span>
                </div>

              </div>
            </template>
          </div>

          <el-button
              v-if="isMock && currentOrderNo && paymentStatus === 'pending'"
              class="mock-pay-btn"
              type="warning"
              plain
              @click="mockPay"
          >模拟支付成功（仅测试环境）</el-button>

          <!-- 底部提示（仅轮询中显示） -->
<!--          <div class="polling-hint" v-if="paymentStatus === 'pending' && payQrCode">-->
<!--            <el-icon class="polling-icon"><Loading /></el-icon>-->
<!--            <span>请使用微信扫描二维码支付</span>-->
<!--          </div>-->
<!--          <div class="expired-hint" v-else-if="paymentStatus === 'expired'">-->
<!--            <span>二维码已过期，请点击「重新生成」刷新</span>-->
<!--          </div>-->
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, markRaw, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {Check, Loading, Close, WarningFilled} from '@element-plus/icons-vue'
import LoginModal from '@/components/LoginModal.vue'
import { EXE_TIERS, pickTierPrice } from '@/constants/pricing'
import { useQrPayment } from '@/composables/useQrPayment'
import RegisterModal from '@/components/RegisterModal.vue'
import BindPhoneDialog from '@/components/BindPhoneDialog.vue'
import QRCode from 'qrcode'
import request from '@/utils/request'
import { ElMessage } from 'element-plus'


// 获取路由参数中的插件ID
const route = useRoute()
const router = useRouter()
const pluginId = ref(parseInt(route.query.id))

// 插件数据
const plugin = ref({
  id: 0,
  name: '',
  price: 0,
  description: '',
  productCode: ''
})

// 加载状态
const loading = ref(false)

// 支付相关数据
const showPayDialog = ref(false)
const selectedDays = ref(null)   // 改为 null，不默认选中任何周期
const payQrCode = ref('')
const isMock = ref(false)
// paymentStatus / currentOrderNo 由 useQrPayment 提供
// paymentStatus 与轮询定时器由下方 useQrPayment 提供，此处不再重复声明。

// 价格响应式变量
const priceSmallCycle = ref(0)
const priceMediumCycle = ref(0)
const priceLargeCycle = ref(0)

// 注册登录相关状态
const showLoginModal = ref(false)
const showRegisterModal = ref(false)
const currentPaymentInfo = ref({})

// 添加滚动锁定状态
const isScrollLocked = ref(false)

// 二维码加载状态（用于遮罩层）
const qrLoading = ref(false)
// 后端返回的过期时间戳（毫秒）
const expireTimestamp = ref(null)
// 过期计时器
const expireTimer = ref(null)
// 倒计时更新定时器（可选）
const countdownInterval = ref(null)

// 获取插件详情
const fetchPluginDetails = async () => {
  loading.value = true
  try {
    // 使用新的接口获取包含价格信息的插件详情
    const response = await request.get(`/plugin/details-with-pricing/${pluginId.value}`)

    if (response.code === '200') {
      plugin.value = {
        id: response.data.id,
        name: response.data.name,
        description: response.data.description || '',
        productCode: response.data.productCode // 获取产品编码
      }

      // 设置各周期价格
      priceSmallCycle.value = parseFloat(response.data.priceSmallCycle || 0)
      priceMediumCycle.value = parseFloat(response.data.priceMediumCycle || 0)
      priceLargeCycle.value = parseFloat(response.data.priceLargeCycle || 0)

      // 检查是否有价格设置
      if (!response.data.hasPricing) {
        // ElMessage.warning('该插件暂未设置价格，请联系管理员')
      }

      // 检查是否获取到产品编码
      // console.log(plugin.value.productCode)
      if (!plugin.value.productCode) {
        // ElMessage.warning('无法获取产品编码，可能影响下载功能')
      }

      return
    }

    console.error('无法解析响应格式:', response.data)

  } catch (error) {
    console.error('获取插件详情失败:', error)
  } finally {
    loading.value = false
  }
}

// 时间周期选项
// 档位天数与取价字段来自 constants/pricing.js（唯一真源），此处保留各自的 UI 文案。
// 注意：第 0 项是免费体验（days: 0），不在定价档位里，因此从 EXE_TIERS 下标 0 开始对应。
const periodOptions = computed(() => {
  const priceOf = (i) => pickTierPrice({
    priceSmallCycle: priceSmallCycle.value,
    priceMediumCycle: priceMediumCycle.value,
    priceLargeCycle: priceLargeCycle.value
  }, EXE_TIERS[i].priceKey)
  return [
    {
      days: 0,
      desc: '免费体验',
      type: 'free',
      duration: '30天',
      originalPrice: 0,
      currentPrice: 0,
      savings: '零成本试用',
      features: [
        '完整功能体验',
        '无使用限制',
        '一键下载安装',
        '适合初次体验用户'
      ],
      badge: '推荐',
      popular: true
    },
    {
      days: 30,
      desc: '月卡套餐',
      type: 'paid',
      duration: '30天',
      originalPrice: priceOf(0),
      currentPrice: priceOf(0),
      savings: '灵活度高',
      features: [
        '适合短期项目',
        '按需灵活购买',
        '性价比适中',
        '随时可升级'
      ],
      badge: '灵活',
      popular: false
    },
    {
      days: 90,
      desc: '季卡套餐',
      type: 'paid',
      duration: '90天',
      originalPrice: priceOf(1),
      currentPrice: priceOf(1),
      savings: '稳定省心',
      features: [
        '性价比最高',
        '长期项目首选',
        '节省10%费用',
        '稳定使用体验'
      ],
      badge: '超值',
      popular: true
    },
    {
      days: 365,
      desc: '年卡套餐',
      type: 'paid',
      duration: '365天',
      originalPrice: priceOf(2),
      currentPrice: priceOf(2),
      savings: '尊享优惠',
      features: [
        '最大优惠力度',
        '企业用户首选',
        '节省20%费用',
        '全年免费更新',
        '优先技术支持'
      ],
      badge: '尊享',
      popular: false
    }
  ]
})

// 存储订单信息
const payOrderInfo = ref({
  outTradeNo: '',
  productName: '',
  amount: 0,
  days: 0,
  timeExpire: ''
})

// 计算价格函数
const calculatePrice = (days) => {
  const period = periodOptions.value.find(p => p.days === days)
  if (period) {
    return period.currentPrice.toFixed(2)
  }
  return '0.00'
}

// 获取选中的周期信息
const getSelectedPeriod = () => {
  return periodOptions.value.find(period => period.days === selectedDays.value) || periodOptions.value[0]
}

// 周期选择事件
const onPeriodChange = async (days) => {
  selectedDays.value = days

  // 查找选中的周期选项
  const selectedPeriod = periodOptions.value.find(period => period.days === days)

  if (selectedPeriod.type === 'free') {
    // 免费体验：直接触发下载，不需要登录
    await handleFreeTrial()
  } else {
    // 付费周期：检查登录状态
    const isLoggedIn = checkUserLogin()

    if (!isLoggedIn) {
      // 未登录：保存支付信息并显示登录弹窗
      currentPaymentInfo.value = {
        days: days,
        period: selectedPeriod,
        plugin: plugin.value
      }
      showLoginModal.value = true
    } else {
      // 已登录：显示支付弹窗
      showPayDialog.value = true
      await handlePaidPeriod(days)
    }
  }

}

// 处理免费体验
const handleFreeTrial = async () => {
  try {
    ElMessage.success('开始免费体验，正在下载最新版本...')

    // 获取产品编码
    if (!plugin.value.productCode) {
      // ElMessage.error('无法获取产品编码')
      return
    }

    // 记录下载信息
    await recordDownload(plugin.value.id);

    // 触发下载最新版本
    window.location.href = `/api/file/download-latest/${plugin.value.productCode}`
  } catch (error) {
    console.error('免费体验选择失败:', error)
    ElMessage.error('下载失败，请重试')
  }
}

// 处理付费周期
const handlePaidPeriod = async (days) => {
  // 如果存在未支付的旧订单，先关闭它
  if (currentOrderNo.value && paymentStatus.value === 'pending') {
    await handleCloseOrder(currentOrderNo.value)
  }

  // 清除当前支付状态和二维码
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  currentOrderNo.value = ''
  qrLoading.value = true  // ★ 新增：显示加载遮罩
  expireTimestamp.value = null  // ★ 新增：重置过期时间

  // 重新生成支付二维码
  await handlePurchase(days)
}

// 生成支付二维码
const handlePurchase = async (days) => {
  selectedDays.value = days
  paymentStatus.value = 'pending'
  payQrCode.value = ''

  try {
    const response = await request.post('/pay/createQrCode', {
      productId: plugin.value.id,
      productName: plugin.value.name,
      days: days,
      amount: calculatePrice(days),
      description: `购买${plugin.value.name} ${days}天使用权`,
      paymentType: 'wechat_native'
    })

    if (response.data.success) {
      // 生成二维码图片
      const qrCodeDataURL = await QRCode.toDataURL(response.data.qrCodeUrl, {
        width: 200,
        margin: 1
      })
      payQrCode.value = qrCodeDataURL
      isMock.value = !!response.data.mock
      qrLoading.value = false  // ★ 修改：加载完成，隐藏遮罩

      // ★ 新增：保存后端返回的过期时间
      const timeExpire = response.data.timeExpire
      if (timeExpire) {
        expireTimestamp.value = new Date(timeExpire).getTime()
      } else {
        // 兜底：15分钟
        expireTimestamp.value = Date.now() + 15 * 60 * 1000
      }

      // 保存订单信息
      payOrderInfo.value = {
        outTradeNo: response.data.outTradeNo,
        productName: plugin.value.name,
        amount: calculatePrice(days),
        days: days,
        timeExpire: response.data.timeExpire || ''
      }
      trackOrder(response.data.outTradeNo)
      // 启动轮询 + 过期计时
      startRenewTimer(response.data.outTradeNo)

      // console.log('支付创建成功：', response.data)
    } else {
      qrLoading.value = false
      // ElMessage.error(response.data.message || '支付请求失败')
    }
  } catch (error) {
    qrLoading.value = false
    console.error('支付请求失败:', error)
    // ElMessage.error('支付请求失败，请重试')
  }
}

// 启动轮询 + 过期计时
// 注：轮询本体已抽到 composables/useQrPayment.js（与另两页共用），
// 这里只保留本页特有的"按后端 timeExpire 计时过期"逻辑。
const startRenewTimer = (outTradeNo) => {
  // 根据后端过期时间计算剩余毫秒数
  const now = Date.now()
  const remaining = expireTimestamp.value ? expireTimestamp.value - now : 15 * 60 * 1000
  const expireDelay = Math.max(remaining, 1000) // 至少1秒

  if (expireTimer.value) {
    clearTimeout(expireTimer.value)
  }
  expireTimer.value = setTimeout(() => {
    if (paymentStatus.value === 'pending') {
      paymentStatus.value = 'expired'
      stopPolling()
      qrLoading.value = false
      // ElMessage.warning('支付二维码已过期，请重新生成')
    }
  }, expireDelay)

  startPolling(outTradeNo)
}

// ★ 新增：支付成功后自动下载
const handleDownloadAfterPayment = () => {
  if (plugin.value.productCode) {
    recordDownload(plugin.value.id).then(() => {
      window.location.href = `/api/file/download-latest/${plugin.value.productCode}`
    })
  } else {
    // ElMessage.error('无法获取产品编码')
  }
  // 延迟关闭弹窗，让用户看到成功状态
  setTimeout(() => {
    showPayDialog.value = false
  }, 3000)
}

// ★ 新增：重新生成二维码
const handleRegenerate = async () => {
  if (currentOrderNo.value) {
    await handleCloseOrder(currentOrderNo.value)
  }
  paymentStatus.value = 'pending'
  payQrCode.value = ''
  currentOrderNo.value = ''
  qrLoading.value = true
  expireTimestamp.value = null
  await handlePurchase(selectedDays.value)
}

// 轮询与关单骨架来自 composables/useQrPayment.js
// （与 PublicPluginDetail / UserProfilePlugins 共用同一份实现）。
// 注意：本页的过期机制是"用 setTimeout 按后端 timeExpire 计时"，
// 与另两页不同，因此 onTick 只做兜底、真正计时仍在本文件的 expireTimer 里。
const {
    paymentStatus,
    currentOrderNo,
    startPolling,
    stopPolling,
    trackOrder,
    closeOrder,
    reset,
} = useQrPayment({
    pollStatusApi: (outTradeNo) => request.get(`/pay/pollStatus/${outTradeNo}`).then(r => r.data),
    closeOrderApi: (outTradeNo) => request.post(`/pay/closeOrder/${outTradeNo}`).then(r => r.data),
    onTick: () => false, // 本页过期由 expireTimer 负责，这里不重复判断
    onSuccess: () => {
        if (expireTimer.value) {
            clearTimeout(expireTimer.value)
            expireTimer.value = null
        }
        ElMessage.success('支付成功！')
        // 延迟1.2秒后自动下载
        setTimeout(() => {
            handleDownloadAfterPayment()
        }, 1200)
    },
    onFail: (error) => console.error('轮询支付状态失败:', error),
})

// 仅测试环境：模拟支付成功
const mockPay = async () => {
  if (!currentOrderNo.value) return
  try {
    await request.post(`/pay/mock/success/${currentOrderNo.value}`)
    ElMessage.success('已模拟支付成功，等待确认...')
  } catch (e) {
    ElMessage.error(e.message || '模拟支付失败')
  }
}

// 取消订单（保留本页原有的关闭后清理逻辑，关单请求走 useQrPayment 注入的实现）
const handleCloseOrder = async (orderNo = null) => {
  const outTradeNo = orderNo || payOrderInfo.value.outTradeNo
  if (!outTradeNo) {
    // ElMessage.warning('没有可关闭的订单')
    return
  }

  try {
    const response = await request.post(`/pay/closeOrder/${outTradeNo}`)
    if (response.data.success) {
      // ElMessage.success('订单已成功关闭')

      // 如果关闭的是当前订单，清除相关信息
      if (outTradeNo === currentOrderNo.value) {
        payOrderInfo.value = {
          outTradeNo: '',
          productName: '',
          amount: 0,
          days: 0,
          timeExpire: ''
        }
        payQrCode.value = ''
        paymentStatus.value = 'pending'
        currentOrderNo.value = ''
        qrLoading.value = false
        expireTimestamp.value = null
      }
    } else {
      // ElMessage.error('关闭订单失败: ' + response.data.message)
    }
  } catch (error) {
    console.error('关闭订单失败:', error)
    // ElMessage.error('关闭订单失败，请重试')
  }
}

// 关闭弹窗
const handleClose = (done) => {
  // 执行清理逻辑
  showPayDialog.value = false
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  qrLoading.value = false
  expireTimestamp.value = null

  if (currentOrderNo.value && paymentStatus.value === 'pending') {
    handleCloseOrder(currentOrderNo.value)
  }
  stopPolling()
  if (expireTimer.value) {
    clearTimeout(expireTimer.value)
    expireTimer.value = null
  }
  selectedDays.value = null

  // 调用 done 关闭弹窗
  if (typeof done === 'function') {
    done()
  }
}

// ★ 新增：直接关闭弹窗（用于自定义关闭按钮）
const closeDialog = () => {
  showPayDialog.value = false
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  qrLoading.value = false
  expireTimestamp.value = null

  if (currentOrderNo.value && paymentStatus.value === 'pending') {
    handleCloseOrder(currentOrderNo.value)
  }
  stopPolling()
  if (expireTimer.value) {
    clearTimeout(expireTimer.value)
    expireTimer.value = null
  }
  selectedDays.value = null
}

// 下载记录
const recordDownload = async (productId) => {
  console.log(productId)
  try {
    await request.post(`/plugin/record/download?productId=${productId}`);
    console.log('下载记录保存成功');
  } catch (error) {
    console.error('保存下载记录失败:', error);
    // 这里不提示用户，避免影响下载体验
  }
};

// 检查用户登录状态
const checkUserLogin = () => {
  const token = localStorage.getItem('auth_token')
  return !!token
}

// 优化的滚动锁定函数
const lockScroll = () => {
  if (isScrollLocked.value) return

  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
  document.body.style.overflow = 'hidden'
  document.body.style.paddingRight = `${scrollBarWidth}px`
  isScrollLocked.value = true
}

const unlockScroll = () => {
  if (!isScrollLocked.value) return

  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  isScrollLocked.value = false
}

// 优化弹窗切换函数，确保无间隙切换
const switchToRegister = () => {
  // 先锁定滚动，再切换弹窗
  lockScroll()
  showLoginModal.value = false
  // 使用 nextTick 确保在下一个 DOM 更新周期打开注册弹窗
  nextTick(() => {
    showRegisterModal.value = true
  })
}

const switchToLogin = () => {
  // 先锁定滚动，再切换弹窗
  lockScroll()
  showRegisterModal.value = false
  nextTick(() => {
    showLoginModal.value = true
  })
}

// 弹窗状态监听器
watch([showLoginModal, showRegisterModal], ([loginVisible, registerVisible]) => {
  if (loginVisible || registerVisible) {
    lockScroll()
  } else {
    // 使用 setTimeout 确保所有弹窗都完全关闭后再解锁滚动
    setTimeout(() => {
      if (!showLoginModal.value && !showRegisterModal.value) {
        unlockScroll()
      }
    }, 50)
  }
})

// 监听插件ID变化
watch(() => route.query.id, (newId) => {
  if (newId) {
    pluginId.value = parseInt(newId)
    fetchPluginDetails()
  }
})

// 弹窗关闭处理函数 - 登录成功处理
const handleLoginSuccess = async (userInfo) => {
  const { paymentInfo } = userInfo

  if (paymentInfo) {
    currentPaymentInfo.value = paymentInfo
    selectedDays.value = paymentInfo.days

    // 添加短暂延迟确保用户信息完全加载
    await new Promise(resolve => setTimeout(resolve, 300));

    showPayDialog.value = true
    handlePaidPeriod(paymentInfo.days)
  }

  showLoginModal.value = false
}
// 弹窗关闭处理函数 - 注册成功处理
const handleRegisterSuccess = async (userInfo) => {
  const { paymentInfo } = userInfo

  if (paymentInfo) {
    currentPaymentInfo.value = paymentInfo
    selectedDays.value = paymentInfo.days

    // 添加短暂延迟确保用户信息完全加载
    await new Promise(resolve => setTimeout(resolve, 300));

    showPayDialog.value = true
    handlePaidPeriod(paymentInfo.days)
  }

  showRegisterModal.value = false
}

onMounted(() => {
  fetchPluginDetails()
})

// 在组件卸载时清除轮询
onBeforeUnmount(() => {
  unlockScroll()

  stopPolling()

  if (expireTimer.value) {
    clearTimeout(expireTimer.value)
    expireTimer.value = null
  }

  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }
})
</script>

<style scoped>
/* ============== 基础容器 / 背景 ============== */
.plugin-pricing-container {
  position: relative;
  min-height: 100vh;
  padding: 0 20px;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
  'PingFang SC', 'Microsoft YaHei', sans-serif;
  background:
      radial-gradient(ellipse 800px 600px at 10% 0%, rgba(47, 91, 255, 0.08), transparent 60%),
      radial-gradient(ellipse 700px 500px at 90% 10%, rgba(245, 158, 11, 0.06), transparent 55%),
      #f7f9fc;
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  animation: glow 8s ease-in-out infinite;
}
.bg-glow--blue {
  top: -120px;
  left: -100px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.35), transparent 70%);
}
.bg-glow--orange {
  top: 80px;
  right: -120px;
  width: 380px;
  height: 380px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.25), transparent 70%);
  animation-delay: 2s;
}

@keyframes glow {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}

/* ============== 顶部标题 ============== */
.pricing-header,
.pricing-content {
  position: relative;
  z-index: 1;
}

.pricing-header {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  padding: 80px 20px 40px;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.header-eyebrow {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #2f5bff;
  background: rgba(47, 91, 255, 0.08);
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 20px;
  border: 1px solid rgba(47, 91, 255, 0.18);
}

.pricing-title {
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 1rem 0;
  background: linear-gradient(135deg, #1e293b 0%, #2f5bff 55%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.pricing-subtitle {
  font-size: 1.15rem;
  color: #475569;
  margin: 0;
  font-weight: 400;
}

/* ============== 定价内容 ============== */
.pricing-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0 100px;
}

.period-selection {
  margin: 40px 0 0;
}

.period-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 20px;
}

/* ============== 周期卡片 ============== */
/* ★ 修改：移除 overflow: hidden，让角标完全显示 */
.period-option {
  position: relative;
  padding: 36px 24px 28px;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 20px;
  cursor: pointer;
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: 460px;
  overflow: visible; /* ★ 修改：允许角标溢出 */
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
  isolation: isolate;
}

.period-option:nth-child(1) { animation-delay: 0.05s; }
.period-option:nth-child(2) { animation-delay: 0.15s; }
.period-option:nth-child(3) { animation-delay: 0.25s; }
.period-option:nth-child(4) { animation-delay: 0.35s; }

.period-option:hover {
  transform: translateY(-8px);
  border-color: rgba(47, 91, 255, 0.4);
  box-shadow: 0 20px 50px rgba(47, 91, 255, 0.16);
}

/* ★ 删除：.period-option:hover .card-top-line 和 .period-option.active .card-top-line */

.period-option.active {
  border-color: transparent;
  background:
      linear-gradient(#ffffff, #ffffff) padding-box,
      linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 20px 48px rgba(47, 91, 255, 0.22);
  transform: translateY(-6px);
}

.period-option.popular-option {
  border-color: rgba(245, 158, 11, 0.35);
}
.period-option.popular-option.active {
  border-color: transparent;
  background:
      linear-gradient(#ffffff, #ffffff) padding-box,
      linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 20px 48px rgba(245, 158, 11, 0.25);
}

.period-option.free-option {
  border-color: rgba(47, 91, 255, 0.35);
}
.period-option.free-option.active {
  border-color: transparent;
  background:
      linear-gradient(#ffffff, #ffffff) padding-box,
      linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 20px 48px rgba(47, 91, 255, 0.25);
}

/* ============== 角标 ============== */
/* ★ 修改：角标 top 值调整为 -16px，确保完全显示 */
.period-badge {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: #ffffff;
  padding: 6px 18px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  z-index: 3;
  white-space: nowrap;
}
.free-option .period-badge {
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  box-shadow: 0 6px 16px rgba(47, 91, 255, 0.4);
}

/* ============== 周期头部 ============== */
.period-header {
  text-align: center;
  margin-bottom: 22px;
}
.period-desc {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}
.period-duration {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

/* ============== 价格区域 ============== */
.price-section {
  text-align: center;
  margin-bottom: 22px;
}
.original-price {
  font-size: 14px;
  color: #94a3b8;
  text-decoration: line-through;
  margin-bottom: 6px;
  font-weight: 500;
}
.current-price {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  letter-spacing: -0.5px;
}
.price-symbol {
  font-size: 18px;
  font-weight: 700;
  vertical-align: super;
  margin-right: 2px;
}
.free-price {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  letter-spacing: -0.5px;
}
.savings {
  font-size: 12px;
  font-weight: 600;
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  padding: 5px 14px;
  border-radius: 999px;
  display: inline-block;
  border: 1px solid rgba(245, 158, 11, 0.2);
}
.savings--free {
  color: #2f5bff;
  background: rgba(47, 91, 255, 0.1);
  border-color: rgba(47, 91, 255, 0.2);
}

/* ============== 功能列表 ============== */
.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 auto 0;
  text-align: left;
  flex-grow: 1;
}
.features-list li {
  display: flex;
  align-items: center;
  padding: 9px 0;
  font-size: 14px;
  color: #475569;
  font-weight: 500;
}
.feature-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%);
  color: #ffffff;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(47, 91, 255, 0.25);
}
.feature-check .el-icon {
  font-size: 13px;
  color: #ffffff;
}
.feature-text {
  flex: 1;
}

/* ============== 操作按钮 ============== */
.action-button {
  position: relative;
  text-align: center;
  padding: 14px 0;
  margin-top: 24px;
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: #ffffff;
  border-radius: 14px;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 1px;
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.period-option:hover .action-button {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(245, 158, 11, 0.4);
}
.free-option .action-button {
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  box-shadow: 0 8px 20px rgba(47, 91, 255, 0.3);
}
.free-option:hover .action-button {
  box-shadow: 0 12px 26px rgba(47, 91, 255, 0.4);
}

/* ============== ★ 修改：支付弹窗样式（完全重写） ============== */

/* 弹窗整体：全背景覆盖 */
:deep(.pricing-pay-dialog) {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.20) !important;
  background: #ffffff !important;
}

:deep(.pricing-pay-dialog .el-dialog__header) {
  padding: 0 !important;
  margin: 0 !important;
  border-bottom: none !important;
  background: transparent !important;
}

:deep(.pricing-pay-dialog .el-dialog__headerbtn) {
  display: none !important;
}

:deep(.pricing-pay-dialog .el-dialog__body) {
  padding: 0 !important;
  background: #ffffff !important;
}

:deep(.pricing-pay-dialog .el-dialog__footer) {
  display: none !important;
}

/* 自定义头部 */
.pay-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.7);
  background: transparent;
}

.header-left {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.plan-name {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.plan-duration {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 22px;
}

.plan-price {
  font-size: 24px;
  font-weight: 800;
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.close-btn {
  font-size: 20px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 4px;
  margin-top: -35px;
  margin-right: -20px;
  border-radius: 4px;
  background: transparent;

  svg {
    stroke-width: 10;      /* 加粗描边 */
    stroke: currentColor; /* 继承颜色 */
  }
}
.close-btn:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #dc2626;
  transform: rotate(90deg);
}

/* 弹窗内容 */
.pay-dialog-body {
  padding: 13px 32px 0;
}

/* 微信支付标识 */
.wechat-pay-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 8px 0;
}
.wechat-icon {
  flex-shrink: 0;
}
.pay-label {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}
.pay-status {
  font-size: 14px;
  font-weight: 500;
  color: #22c55e;
}

/* 二维码容器 */
.qrcode-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 240px;
}

.mock-pay-btn {
  width: 100%;
  margin-top: 10px;
}

.qrcode-container {
  position: relative;
  display: inline-block;
  padding: 12px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(226, 232, 240, 0.6);
  transition: box-shadow 0.3s ease;
}
.qrcode-container:hover {
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(47, 91, 255, 0.15);
}

.qrcode-image {
  display: block;
  width: 200px;
  height: 200px;
  border-radius: 8px;
  background: #f7f9fc;
}

/* ★ 修改：加载占位（二维码生成前，仅显示spinner，无文字） */
.qr-placeholder {
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: #f7f9fc;
  border-radius: 8px;
}
.qr-placeholder .el-icon.is-loading {
  animation: rotating 0.9s linear infinite;
}

/* ★ 修改：遮罩层（加载/过期/成功） */
.qr-mask {
  position: absolute;
  top: 12px;
  left: 12px;
  width: calc(100% - 24px);
  height: calc(100% - 24px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(4px);
  animation: fadeInUp 0.35s ease both;
}

/* 加载遮罩 */
.mask-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #2f5bff;
}

.mask-spinner .el-icon.is-loading {
  animation: rotating 0.8s linear infinite;
}

/* 过期遮罩 */
.expired-icon {
  font-size: 40px;
  color: #f59e0b;
}
.expired-text {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.expired-mask-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  transition: background 0.2s ease;
}
.expired-mask-content:hover {
  background: rgba(245, 158, 11, 0.05);
}

.qr-mask .el-button {
  margin-top: 4px;
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  border: none;
  color: #ffffff;
  font-weight: 600;
  padding: 8px 24px;
  border-radius: 20px;
  box-shadow: 0 4px 14px rgba(47, 91, 255, 0.30);
  transition: all 0.3s ease;
}
.qr-mask .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(47, 91, 255, 0.35);
}

/* 支付成功遮罩 */
.success-mask {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 8px;   /* 与原有间距保持一致 */
}

.success-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.35);
  animation: pulse 1.6s ease-in-out infinite;
}
.success-text {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}
.success-sub {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* 底部提示 */
.polling-hint {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
}
.polling-hint .polling-icon {
  font-size: 16px;
  color: #2f5bff;
  animation: rotating 1.6s linear infinite;
}
.expired-hint {
  margin-top: 16px;
  color: #f59e0b;
  font-size: 13px;
  font-weight: 500;
}

/* 动画 */
@keyframes rotating {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* ============== 响应式 ============== */
@media (max-width: 1200px) {
  .period-options {
    grid-template-columns: repeat(2, 1fr);
  }
  .pricing-title {
    font-size: 2.4rem;
  }
}

@media (max-width: 768px) {
  .plugin-pricing-container {
    padding: 0 15px;
  }
  .pricing-header {
    padding: 60px 15px 30px;
  }
  .period-options {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .pricing-title {
    font-size: 2rem;
  }
  .pricing-subtitle {
    font-size: 1rem;
  }
  .period-option {
    padding: 30px 20px 24px;
    min-height: auto;
  }
  .pricing-content {
    padding: 10px 0 60px;
  }
}

@media (max-width: 520px) {
  .pricing-title {
    font-size: 1.75rem;
  }
  .header-eyebrow {
    font-size: 10px;
    letter-spacing: 3px;
    padding: 5px 12px;
  }
  .current-price,
  .free-price {
    font-size: 28px;
  }

  /* 弹窗响应式 */
  .pay-dialog-header {
    padding: 16px 18px 12px;
    flex-wrap: wrap;
    gap: 6px 0;
  }
  .plan-name {
    font-size: 17px;
  }
  .plan-price {
    font-size: 20px;
  }
  .pay-dialog-body {
    padding: 20px 18px 24px;
  }
  .qrcode-image,
  .qr-placeholder {
    width: 160px;
    height: 160px;
  }
  .qrcode-container {
    padding: 10px;
  }
  .qr-mask {
    top: 10px;
    left: 10px;
    width: calc(100% - 20px);
    height: calc(100% - 20px);
  }
  .success-circle {
    width: 52px;
    height: 52px;
  }
  .success-circle .el-icon {
    font-size: 30px !important;
  }
}
</style>
